import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBackIosNew,
  ArrowForwardIos,
  Check,
  Delete,
  Edit,
  ReceiptLong,
} from '@mui/icons-material';
import {
  buildBulkReconciliationDrafts,
  createQuickReconciliationNote,
  reconciliationStateForInvoice,
  validateQuickReconciliationInput,
} from '../../utils/quickReconciliation';

const todayLocalDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatMoney = (value) => {
  try {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
    }).format(Number(value || 0));
  } catch {
    return `Q ${Number(value || 0).toFixed(2)}`;
  }
};

export function QuickReconciliationNoteDialog({
  open,
  invoice,
  initialNote = null,
  availableAmount = 0,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState({
    number: '',
    date: todayLocalDate(),
    amount: 0,
    reason: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      number: initialNote?.number || '',
      date: String(initialNote?.date || todayLocalDate()).slice(0, 10),
      amount: Number(initialNote?.amount ?? availableAmount ?? 0),
      reason: initialNote?.reason || '',
    });
    setError('');
  }, [open, initialNote, availableAmount]);

  const preview = useMemo(() => {
    const note = createQuickReconciliationNote({ invoice, ...form });
    const allocationAmount = Math.min(
      Number(note.amount || 0),
      Math.max(0, Number(availableAmount || 0))
    );
    return {
      allocationAmount,
      remainingCreditAmount: Math.max(0, Number(note.amount || 0) - allocationAmount),
    };
  }, [invoice, form, availableAmount]);

  const submit = async () => {
    const validationError = validateQuickReconciliationInput(form, todayLocalDate());
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      const note = {
        ...createQuickReconciliationNote({ invoice, ...form }),
        allocationAmount: preview.allocationAmount,
        remainingCreditAmount: preview.remainingCreditAmount,
      };
      const saved = await onSave?.(note);
      if (saved !== false) onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {initialNote ? 'Editar nota de crédito' : 'Capturar nota de crédito'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Factura #{invoice?.number || invoice?.id || '-'}
        </Typography>
        <Typography variant="body2" color="warning.main" fontWeight={600}>
          Pendiente de conciliación: {formatMoney(availableAmount)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            autoFocus
            required
            size="small"
            label="Número de NC"
            value={form.number}
            onChange={(event) => setForm((prev) => ({ ...prev, number: event.target.value }))}
          />
          <TextField
            required
            size="small"
            type="date"
            label="Fecha"
            value={form.date}
            inputProps={{ max: todayLocalDate() }}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setForm((prev) => ({ ...prev, date: event.target.value }))}
          />
          <TextField
            required
            size="small"
            type="number"
            label="Monto"
            value={form.amount}
            inputProps={{ min: 0.01, step: 0.01 }}
            onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            label="Observación (opcional)"
            value={form.reason}
            onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
          />
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Conciliado</Typography>
              <Typography variant="caption" fontWeight={700}>{formatMoney(preview.allocationAmount)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Saldo de la NC</Typography>
              <Typography variant="caption" color={preview.remainingCreditAmount > 0 ? 'warning.main' : 'text.secondary'} fontWeight={700}>
                {formatMoney(preview.remainingCreditAmount)}
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Validando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function QuickReconciliationManagerDialog({
  open,
  invoice,
  notes = [],
  state,
  onClose,
  onAdd,
  onEdit,
  onDelete,
}) {
  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Notas de factura #{invoice?.number || invoice?.id}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMoney(state?.reconciled)} conciliado · {formatMoney(state?.remaining)} restante
            </Typography>
          </Box>
          <Button size="small" startIcon={<Add />} onClick={onAdd}>Agregar NC</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          {notes.length === 0 ? (
            <Alert severity="info">Aún no hay notas capturadas para esta factura.</Alert>
          ) : notes.map(({ note, index }) => (
            <Paper key={`${note.number}-${index}`} variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>NC #{note.number}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {String(note.date || '').slice(0, 10)} · NC {formatMoney(note.amount)} · Conciliado {formatMoney(note.allocationAmount)}
                  </Typography>
                  {Number(note.remainingCreditAmount || 0) > 0 ? (
                    <Typography variant="caption" display="block" color="warning.main">
                      Saldo NC {formatMoney(note.remainingCreditAmount)}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row">
                  <IconButton size="small" onClick={() => onEdit?.(index)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => onDelete?.(index)}><Delete fontSize="small" /></IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

export function BulkReconciliationNotesDialog({ open, invoices = [], notes = [], onClose, onSave }) {
  const drafts = useMemo(
    () => buildBulkReconciliationDrafts(invoices, notes, todayLocalDate()),
    [invoices, notes, open]
  );
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(drafts);
    setError('');
  }, [open, drafts]);

  const updateRow = (invoiceId, field, value) => {
    setRows((prev) => prev.map((row) => (
      Number(row.invoiceId) === Number(invoiceId) ? { ...row, [field]: value } : row
    )));
  };

  const submit = async () => {
    if (!rows.length) return;
    if (rows.some((row) => !String(row.number || '').trim() || !row.date || Number(row.amount || 0) <= 0)) {
      setError('Completa número, fecha y monto de todas las facturas pendientes.');
      return;
    }
    const numbers = rows.map((row) => String(row.number).trim().toLowerCase());
    if (new Set(numbers).size !== numbers.length) {
      setError('Los números de NC capturados no pueden repetirse.');
      return;
    }
    setSaving(true);
    try {
      const saved = await onSave?.(rows.map((row) => createQuickReconciliationNote(row)));
      if (saved !== false) onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Capturar notas pendientes</DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
        {rows.length === 0 ? <Alert severity="success">Todas las facturas están conciliadas.</Alert> : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Factura</TableCell>
                  <TableCell align="right">Pendiente</TableCell>
                  <TableCell>Número NC</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.invoiceId}>
                    <TableCell>#{row.invoice?.number || row.invoiceId}</TableCell>
                    <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                    <TableCell>
                      <TextField size="small" value={row.number} onChange={(event) => updateRow(row.invoiceId, 'number', event.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="date" value={row.date} inputProps={{ max: todayLocalDate() }} onChange={(event) => updateRow(row.invoiceId, 'date', event.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={row.amount} inputProps={{ min: 0.01, step: 0.01 }} onChange={(event) => updateRow(row.invoiceId, 'amount', event.target.value)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || rows.length === 0}>
          {saving ? 'Validando…' : `Confirmar ${rows.length} NC`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function QuickReconciliationPanel({
  invoices = [],
  notes = [],
  onAdd,
  onManage,
  onOpenBulk,
  onBack,
  onContinue,
  continueLabel = 'Continuar',
}) {
  const states = useMemo(() => invoices.map((invoice) => ({
    invoice,
    state: reconciliationStateForInvoice(invoice, notes),
  })), [invoices, notes]);
  const allComplete = states.length > 0 && states.every(({ state }) => state.complete);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>Conciliación de notas de crédito</Typography>
          <Typography variant="body2" color="text.secondary">Captura las NC contra los ajustes pendientes de cada factura.</Typography>
        </Box>
        <Button variant="contained" startIcon={<ReceiptLong />} onClick={onOpenBulk}>
          Capturar notas pendientes
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Factura</TableCell>
              <TableCell align="right">Saldo factura</TableCell>
              <TableCell align="right">Pendiente conciliación</TableCell>
              <TableCell align="right">NC capturadas</TableCell>
              <TableCell align="right">Restante</TableCell>
              <TableCell align="center">Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {states.map(({ invoice, state }) => (
              <TableRow key={invoice.id} sx={{ bgcolor: state.complete ? 'success.50' : undefined }}>
                <TableCell>#{invoice.number || invoice.id}</TableCell>
                <TableCell align="right">{formatMoney(invoice.saldoNum ?? invoice.totalNum)}</TableCell>
                <TableCell align="right">{formatMoney(state.pending)}</TableCell>
                <TableCell align="right">
                  {state.count ? `${state.count} NC · ${formatMoney(state.reconciled)}` : formatMoney(0)}
                  {state.excess > 0 ? <Typography variant="caption" display="block" color="warning.main">Saldo NC {formatMoney(state.excess)}</Typography> : null}
                </TableCell>
                <TableCell align="right">{formatMoney(state.remaining)}</TableCell>
                <TableCell align="center">
                  {state.complete ? (
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                      <Chip size="small" color="success" icon={<Check />} label="Conciliada" />
                      <Button size="small" onClick={() => onManage?.(invoice)}>Ver / editar</Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Button size="small" variant="outlined" onClick={() => onAdd?.(invoice)}>
                        Capturar NC
                      </Button>
                      {state.count ? <Button size="small" onClick={() => onManage?.(invoice)}>Ver / editar</Button> : null}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button variant="outlined" color="inherit" startIcon={<ArrowBackIosNew />} onClick={onBack}>Atrás</Button>
        <Button variant="contained" endIcon={<ArrowForwardIos />} disabled={!allComplete} onClick={onContinue}>
          {continueLabel}
        </Button>
      </Stack>
    </Box>
  );
}
